import { AssignDeckToRoundDialog } from "./-components/assign-deck-to-round-dialog"
import { EditQuizDialog } from "./-components/edit-quiz-dialog"
import { DeleteQuizDialog } from "./-components/delete-quiz-dialog"
import { PublishResultsDialog } from "./-components/publish-results-dialog"
import { UnpublishResultsDialog } from "./-components/unpublish-results-dialog"
import { QuizInstancesTab } from "./-components/quiz-instances-tab"
import { useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { z } from "zod"
import { ChevronLeft, Pencil, Plus, Trash2 } from "lucide-react"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { formatDateInLocalTimezone, formatInLocalTimezone } from "@/lib/timezone-utils"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

const quizDetailSearchSchema = z.object({
  tab: z.string().optional(),
  status: z.string().optional(),
})

export const Route = createFileRoute("/(admin)/admin/quizzes/$quizId")({
  component: RouteComponent,
  validateSearch: (search) => quizDetailSearchSchema.parse(search),
})

function RouteComponent() {
  const { quizId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [selectedRound, setSelectedRound] = useState<number | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const activeTab = search.tab ?? "rounds"
  const statusFilter = search.status

  const quiz = useQuery(
    api.quizzes.handlers.getById,
    quizId ? { quizId: quizId as Id<"quizzes"> } : "skip"
  )
  const decksForQuiz = useQuery(
    api.decks.handlers.listDecksForQuiz,
    quizId ? { quizId: quizId as Id<"quizzes"> } : "skip"
  )
  if (quiz === undefined || decksForQuiz === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (quiz === null) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <h1 className="text-3xl font-bold">Quiz not found</h1>
        <p className="text-muted-foreground">
          The quiz you're looking for doesn't exist.
        </p>
      </div>
    )
  }

  const rounds = Array.from({ length: quiz.totalRounds }, (_, i) => i + 1)

  // Create a map of round number to deck
  const decksByRound: Record<number, (typeof decksForQuiz)[0] | undefined> = {}
  for (const deck of decksForQuiz) {
    if (deck.roundNumber !== undefined) {
      decksByRound[deck.roundNumber] = deck
    }
  }

  const quizTypeColors = {
    daily: "bg-blue-500",
    specialist: "bg-purple-500",
    teams: "bg-green-500",
    pairs: "bg-orange-500",
    champions: "bg-red-500",
  } as const

  const handleAssignDeck = (roundNumber: number) => {
    setSelectedRound(roundNumber)
    setAssignDialogOpen(true)
  }

  const handleTabChange = (value: string) => {
    void navigate({
      to: "/admin/quizzes/$quizId",
      params: { quizId },
      search: { tab: value },
    })
  }

  const handleStatusFilterChange = (status: string) => {
    void navigate({
      to: "/admin/quizzes/$quizId",
      params: { quizId },
      search: {
        tab: activeTab,
        status: status === "all" ? undefined : status,
      },
    })
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/quizzes">
              <ChevronLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{quiz.name}</h1>
            <p className="text-sm text-muted-foreground">
              Manage rounds and review quiz instances
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditDialogOpen(true)
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit Quiz
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              setDeleteDialogOpen(true)
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Quiz
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quiz Information</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Type</p>
            <Badge className={quizTypeColors[quiz.type]} variant="default">
              {quiz.type}
            </Badge>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Start Date</p>
            <p className="font-medium">
              {formatDateInLocalTimezone(quiz.startAt)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">End Date</p>
            <p className="font-medium">
              {quiz.endAt ? formatDateInLocalTimezone(quiz.endAt) : "—"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Rounds</p>
            <p className="font-medium">{quiz.totalRounds}</p>
          </div>
          {quiz.quarter ? (
            <div>
              <p className="text-sm text-muted-foreground">Quarter</p>
              <p className="font-medium">{quiz.quarter.name}</p>
            </div>
          ) : null}
          {quiz.subject ? (
            <div>
              <p className="text-sm text-muted-foreground">Subject</p>
              <Badge variant="outline">{quiz.subject}</Badge>
            </div>
          ) : null}
          <div>
            <p className="text-sm text-muted-foreground">Results Status</p>
            {quiz.resultsPublishedAt ? (
              <div className="flex items-center gap-2">
                <Badge className="bg-green-500">Published</Badge>
                <span className="text-xs text-muted-foreground">
                  {formatInLocalTimezone(quiz.resultsPublishedAt)}
                </span>
              </div>
            ) : (
              <Badge variant="secondary">Not Published</Badge>
            )}
          </div>
          <div className="col-span-2 md:col-span-4 pt-2 border-t">
            {quiz.resultsPublishedAt ? (
              <UnpublishResultsDialog
                quizId={quizId as Id<"quizzes">}
                quizName={quiz.name}
              />
            ) : (
              <PublishResultsDialog
                quizId={quizId as Id<"quizzes">}
                quizName={quiz.name}
                quizType={quiz.type}
              />
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="rounds">Rounds</TabsTrigger>
          <TabsTrigger value="instances">Instances</TabsTrigger>
        </TabsList>

        <TabsContent value="rounds">
          <Card>
            <CardHeader>
              <CardTitle>Rounds & Deck Assignments</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {rounds.map((roundNumber) => {
                const assignedDeck = decksByRound[roundNumber]

                return (
                  <div
                    key={roundNumber}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold">Round {roundNumber}</h3>
                        {assignedDeck !== undefined ? (
                          <Badge variant="default" className="bg-green-500">
                            Assigned
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Empty</Badge>
                        )}
                      </div>
                      {assignedDeck !== undefined ? (
                        <div className="mt-2 text-sm text-muted-foreground">
                          <p>
                            <strong>Deck:</strong> {assignedDeck.name}
                          </p>
                          <p>
                            <strong>Questions:</strong>{" "}
                            {assignedDeck.questionCount}
                          </p>
                        </div>
                      ) : (
                        <p className="mt-2 text-sm text-muted-foreground">
                          No deck assigned to this round yet
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {assignedDeck !== undefined ? (
                        <>
                          <Button variant="outline" size="sm" asChild>
                            <Link
                              to="/admin/quizzes/$quizId/$roundNumber"
                              params={{
                                quizId,
                                roundNumber: roundNumber.toString(),
                              }}
                            >
                              View Questions
                            </Link>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              handleAssignDeck(roundNumber)
                            }}
                          >
                            Change Deck
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            handleAssignDeck(roundNumber)
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Assign Deck
                        </Button>
                      )}
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="instances">
          <QuizInstancesTab
            quizId={quizId as Id<"quizzes">}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
          />
        </TabsContent>
      </Tabs>

      {selectedRound !== null && (
        <AssignDeckToRoundDialog
          quizId={quizId as Id<"quizzes">}
          roundNumber={selectedRound}
          currentDeckId={decksByRound[selectedRound]?._id ?? undefined}
          open={assignDialogOpen}
          onOpenChange={setAssignDialogOpen}
        />
      )}

      <EditQuizDialog
        quiz={quiz}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />
      <DeleteQuizDialog
        quiz={quiz}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      />
    </div>
  )
}
